// src/pages/dashboards/user/UserClaims.jsx
// Reimbursement claims: list, create a draft against a linked company, manage
// items, submit / reopen / mark paid / cancel / delete.

import React, { useEffect, useState } from "react";
import {
  listClaims, getClaim, linkedCompanies, createClaim, addClaimItems,
  removeClaimItem, submitClaim, reopenClaim, markClaimPaid, cancelClaim, deleteClaim,
  uploadClaimItemImage, getClaimMessages, sendClaimMessage,
} from "../../../api/user";
import { Card, Button, Badge, Loading, EmptyState, Modal, Field, Input, Select } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";
import { money, titleCase } from "../../../lib/format";

const STATUS_TONE = {
  draft: "gray", submitted: "blue", queried: "amber", paid: "green", rejected: "red", cancelled: "gray",
};

export default function UserClaims() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => listClaims().then(setRows).catch((e) => toast.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Company claims</h1>
        <Button onClick={() => setCreating(true)}>New claim</Button>
      </div>

      <Card className="overflow-hidden">
        {!rows ? <Loading label="Loading claims" /> : rows.length === 0 ? (
          <EmptyState title="No claims yet" hint="Create a claim to get reimbursed by a linked company." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Company</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Reference</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}
                      className="border-b border-tb-border dark:border-tb-d-border last:border-0 hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset cursor-pointer transition-colors"
                      onClick={() => setOpenId(c.id)}>
                    <td className="px-4 py-3 font-medium">{c.company_name || "—"}</td>
                    <td className="px-4 py-3 text-tb-muted hidden sm:table-cell">{c.reference || "—"}</td>
                    <td className="px-4 py-3"><Badge tone={STATUS_TONE[c.status]}>{titleCase(c.status)}</Badge></td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{money(c.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && <CreateClaimModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); load(); setOpenId(id); }} />}
      {openId && <ClaimModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function CreateClaimModal({ onClose, onCreated }) {
  const toast = useToast();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [reference, setReference] = useState("");
  const [items, setItems] = useState([{ label: "", amount: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { linkedCompanies().then(setCompanies).catch(() => {}); }, []);

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addRow = () => setItems((arr) => [...arr, { label: "", amount: "" }]);
  const delRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!companyId) { toast.error("Pick a company"); return; }
    const cleaned = items
      .filter((it) => it.label.trim() && it.amount)
      .map((it) => ({ label: it.label.trim(), amount: parseFloat(it.amount) }));
    if (!cleaned.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const claim = await createClaim({ company_id: companyId, reference: reference.trim() || undefined, items: cleaned });
      toast.success("Claim created");
      onCreated(claim.id);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="New claim" width="max-w-xl" bodyClassName="p-6">
      <div className="space-y-4">
        {companies.length === 0 ? (
          <p className="text-sm text-tb-muted">
            You're not linked to any company yet. Link to one in the app (via a connect code) to submit claims.
          </p>
        ) : (
          <>
            <Field label="Company">
              <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value="">Select…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Reference" hint="optional"><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Client lunch May" /></Field>

            <div>
              <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Items</p>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="flex gap-2">
                    <Input placeholder="Description" value={it.label} onChange={(e) => setItem(i, "label", e.target.value)} />
                    <Input type="number" step="0.01" min="0" placeholder="0.00" value={it.amount}
                           onChange={(e) => setItem(i, "amount", e.target.value)} className="w-32" />
                    {items.length > 1 && (
                      <button onClick={() => delRow(i)} className="text-red-600 text-sm px-2 shrink-0">✕</button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addRow} className="mt-2 text-xs text-tb-brand hover:underline">+ Add item</button>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={saving}>Create claim</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function ClaimModal({ id, onClose, onChanged }) {
  const toast = useToast();
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
 const [newItem, setNewItem] = useState({ label: "", amount: "" });
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    getClaim(id).then(setClaim).catch((e) => toast.error(e.message));
    getClaimMessages(id).then(setMessages).catch(() => setMessages([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const sendMsg = async () => {
    const body = msgInput.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const msg = await sendClaimMessage(id, body);
      setMessages((m) => [...m, msg]);
      setMsgInput("");
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addItem = async () => {
    if (!newItem.label.trim() || !newItem.amount) { toast.error("Description and amount required"); return; }
    await act(() => addClaimItems(id, [{ label: newItem.label.trim(), amount: parseFloat(newItem.amount) }]), "Item added");
    setNewItem({ label: "", amount: "" });
  };

  if (!claim) return <Modal open onClose={onClose} title="Claim" width="max-w-2xl"><Loading label="Loading claim" /></Modal>;

  const isDraft = claim.status === "draft";

  return (
    <Modal open onClose={onClose} title={claim.company_name || "Claim"} width="max-w-2xl" bodyClassName="p-6">
      <div className="space-y-5">
       <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[claim.status]}>{titleCase(claim.status)}</Badge>
          {claim.reference && <span className="text-sm text-tb-muted">{claim.reference}</span>}
          <span className="ml-auto text-lg font-bold tabular-nums">{money(claim.total)}</span>
        </div>

        {/* company query / rejection reason */}
        {claim.company_note && (claim.status === "queried" || claim.status === "rejected") && (
          <div className={`rounded-xl border p-3 text-sm ${
            claim.status === "queried"
              ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
              : "border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
          }`}>
            <p className="font-semibold mb-0.5">
              {claim.status === "queried" ? "Query from the company" : "Rejection reason"}
            </p>
            <p>{claim.company_note}</p>
          </div>
        )}

        {/* items */}
        <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border">
         {(claim.items || []).map((it) => (
            <div key={it.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="min-w-0 truncate">
                {it.label}
                {it.has_image && <span className="ml-2 text-xs text-tb-brand">photo ✓</span>}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                <span className="tabular-nums">{money(it.amount)}</span>
                {isDraft && (
                  <label className="text-tb-brand text-xs hover:underline cursor-pointer">
                    {it.has_image ? "Replace photo" : "Add photo"}
                    <input type="file" accept="image/*" className="hidden"
                           onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) act(() => uploadClaimItemImage(id, it.id, f), "Photo added"); }} />
                  </label>
                )}
                {isDraft && (
                  <button onClick={() => act(() => removeClaimItem(id, it.id), "Item removed")} disabled={busy}
                          className="text-red-600 text-xs hover:underline">Remove</button>
                )}
              </div>
            </div>
          ))}
          {(!claim.items || claim.items.length === 0) && <p className="px-3 py-3 text-sm text-tb-muted">No items.</p>}
        </div>

        {/* add item (draft only) */}
        {isDraft && (
          <div className="flex gap-2">
            <Input placeholder="Add item description" value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} />
            <Input type="number" step="0.01" min="0" placeholder="0.00" value={newItem.amount}
                   onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })} className="w-32" />
            <Button variant="subtle" onClick={addItem} disabled={busy}>Add</Button>
          </div>
        )}

        {/* message thread */}
        <div className="rounded-xl border border-tb-border dark:border-tb-d-border p-3 space-y-2">
          <p className="text-xs font-medium text-tb-muted">Messages</p>
          {messages.length === 0 ? (
            <p className="text-sm text-tb-muted">No messages yet.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-2">
              {messages.map((m) => {
                const mine = m.author_role === "user";
                return (
                  <div key={m.id} className={`rounded-lg px-3 py-2 text-sm max-w-[85%] ${mine ? "ml-auto bg-tb-brand/10" : "bg-tb-bg-2 dark:bg-tb-d-inset"}`}>
                    <span className={`text-[11px] font-medium ${mine ? "text-tb-brand" : "text-tb-muted"}`}>
                      {mine ? "You" : (m.author_name || "Company")} · {new Date(m.created_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <p className="mt-0.5">{m.body}</p>
                  </div>
                );
              })}
            </div>
          )}
          {claim.status !== "cancelled" && (
            <div className="flex gap-2 pt-1">
              <input value={msgInput} onChange={(e) => setMsgInput(e.target.value)}
                     placeholder="Write a message…"
                     onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMsg())}
                     className="flex-1 rounded-lg border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset px-3 py-2 text-sm outline-none focus:border-tb-brand" />
              <Button variant="subtle" onClick={sendMsg} disabled={sending || !msgInput.trim()}>Send</Button>
            </div>
          )}
        </div>

        {/* actions */}
        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-tb-border dark:border-tb-d-border">
          {isDraft && <Button onClick={() => act(() => submitClaim(id), "Claim submitted")} disabled={busy}>Submit</Button>}
          {["submitted", "queried"].includes(claim.status) && (
            <Button variant="subtle" onClick={() => act(() => reopenClaim(id), "Reopened to draft")} disabled={busy}>Reopen</Button>
          )}
         
          {!["paid", "cancelled"].includes(claim.status) && (
            <Button variant="ghost" className="text-red-600" onClick={() => act(() => cancelClaim(id), "Claim cancelled")} disabled={busy}>Cancel claim</Button>
          )}
          {isDraft && (
            <Button variant="ghost" className="text-red-600"
                    onClick={() => { if (window.confirm("Delete this draft permanently?")) act(() => deleteClaim(id), "Draft deleted").then(onClose); }}
                    disabled={busy}>Delete</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}